import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import InfoNotice from '../../Components/InfoNotice';
import { formatMetaSyncTime } from '../metaAdsConnection';
import {
  metaAdsBodyTextSx,
  metaAdsHeroButtonSx,
  metaAdsMutedTextSx,
  metaAdsPrimaryButtonLoadingSx,
  metaAdsPrimaryButtonSx,
} from './metaAdsUi.styles';

const EDITOR_COPY = {
  pending: {
    title: 'Solo el dueño del espacio puede conectar Meta',
    description: 'Cuando esté conectada vas a ver las campañas acá.',
  },
  reconnect: {
    title: 'Se cortó la conexión con Meta',
    description: 'Tus datos anteriores siguen acá. Avisale al dueño del espacio para que la reconecte.',
  },
  none_available: {
    title: 'Falta elegir una cuenta publicitaria en Meta',
    description: 'Avisale al dueño del espacio para que vuelva a conectar y marque la cuenta.',
  },
};

/**
 * Botón «Conectar con Meta» / «Reconectar» (solo dueño). Mientras conecta
 * queda deshabilitado con spinner.
 *
 * @param {{ label: string, connecting: boolean, onConnect: () => void }} props
 */
export function MetaConnectButton({ label, connecting, onConnect, fullWidth = false }) {
  return (
    <Button
      variant="contained"
      disableElevation
      onClick={onConnect}
      disabled={connecting}
      startIcon={connecting ? <CircularProgress size={14} color="inherit" /> : null}
      sx={[
        fullWidth ? metaAdsHeroButtonSx : metaAdsPrimaryButtonSx,
        connecting ? metaAdsPrimaryButtonLoadingSx : null,
      ]}
    >
      {connecting ? 'Conectando…' : label}
    </Button>
  );
}

/**
 * Cuerpo del paso «Conectar tu cuenta de Meta» (P1). El dueño
 * (`viewerCanManage`) ve qué hace Terrio y el botón; el editor, a quién pedirle.
 *
 * @param {{ step: object, connection: object, connecting: boolean, onConnect: () => void }} props
 */
export default function MetaConnectStep({ step, connection, connecting, onConnect }) {
  const canManage = Boolean(connection?.viewerCanManage);

  if (step.status === 'done') {
    const syncLabel = formatMetaSyncTime(connection?.lastSyncAt);
    return (
      <Typography sx={metaAdsMutedTextSx}>
        {syncLabel ? `Última actualización: ${syncLabel}.` : 'Todavía no trajimos datos de Meta.'}
      </Typography>
    );
  }

  if (!canManage) {
    const copy = EDITOR_COPY[step.status] || EDITOR_COPY.pending;
    return <InfoNotice title={copy.title} description={copy.description} data-testid="meta-ads-editor-notice" />;
  }

  if (step.status === 'reconnect') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={metaAdsBodyTextSx}>
          Se cortó la conexión con Meta. Tus datos anteriores siguen acá; reconectá para seguir viendo resultados.
        </Typography>
        <MetaConnectButton fullWidth label="Reconectar" connecting={connecting} onConnect={onConnect} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography sx={metaAdsMutedTextSx}>
        Terrio solo lee tus campañas y resultados: nunca publica ni gasta sin que lo confirmes.
      </Typography>
      <MetaConnectButton fullWidth label="Conectar con Meta" connecting={connecting} onConnect={onConnect} />
    </Box>
  );
}
